import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/layout/Reveal";
import { SERVICES } from "@/lib/site";

/**
 * PrestationsCrossLinks — Cross-link block that surfaces the 4 core prestations
 * and the Réalisations page. Used on secondary pages (Entreprise, Contact) to
 * strengthen internal linking and guide the user toward high-value pages.
 */
export default function PrestationsCrossLinks({
    kicker = "Découvrir aussi",
    title = "Nos prestations à Genève et en Suisse romande",
    showRealisations = true,
    dataTestId = "prestations-cross-links",
}) {
    return (
        <section className="bg-white border-t border-concrete-200" data-testid={dataTestId}>
            <div className="rcps-container py-16 md:py-20">
                <Reveal>
                    <p className="kicker text-navy-700">
                        <span className="hairline" /> {kicker}
                    </p>
                    <h2 className="mt-3 font-heading text-2xl md:text-3xl font-semibold text-navy-700 tracking-tight">
                        {title}
                    </h2>
                </Reveal>

                <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {SERVICES.map((s, i) => (
                        <Reveal key={s.slug} delay={(i % 4) * 0.05}>
                            <Link
                                to={`/prestations/${s.slug}`}
                                data-testid={`cross-link-${s.slug}`}
                                className="group h-full flex flex-col justify-between rounded-md border border-concrete-200 p-6 hover:border-navy-700 transition"
                            >
                                <div>
                                    <span className="font-heading text-navy-700 font-semibold text-base md:text-lg leading-snug">
                                        {s.title}
                                    </span>
                                    <p className="mt-3 text-sm text-navy-700/70 leading-relaxed">
                                        {s.short}
                                    </p>
                                </div>
                                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-navy-700">
                                    Découvrir
                                    <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition" />
                                </span>
                            </Link>
                        </Reveal>
                    ))}
                </div>

                {showRealisations && (
                    <Reveal delay={0.1}>
                        <div className="mt-6 flex flex-wrap items-center gap-4">
                            <Link
                                to="/realisations"
                                data-testid="cross-link-realisations"
                                className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-concrete-200 text-navy-700 font-semibold hover:border-navy-700 hover:bg-concrete-50 transition"
                            >
                                Voir nos réalisations
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/entreprise"
                                data-testid="cross-link-entreprise"
                                className="inline-flex items-center gap-2 h-11 px-5 rounded-md text-navy-700 font-semibold hover:underline underline-offset-4"
                            >
                                Découvrir l’entreprise
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </Reveal>
                )}
            </div>
        </section>
    );
}
